import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import FaqList from "@/components/feature/FaqList";
import { useSeo } from "@/hooks/useSeo";
import { faqs, faqGroups } from "@/mocks/faqs";

export default function Faq() {
  useSeo({
    title: "常见问题 - 收藏、书签与关注怎么用 | 5看料网",
    description:
      "5看料网常见问题解答：收藏与书签的区别、保存的内容会不会保留、如何对感兴趣的话题持续关注，以及内容更新与隐私相关的说明。",
    keywords: "5看料网常见问题,收藏怎么用,书签和标记区别,话题关注,内容更新",
    path: "/faq",
  });

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://tpon.cn/" },
      { "@type": "ListItem", position: 2, name: "常见问题", item: "https://tpon.cn/faq" },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-foreground-800">常见问题</li>
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              常见问题
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-7 text-foreground-600">
              关于收藏、保存、书签、记录、标记与关注的使用方式，以及内容更新与隐私说明，
              都能在下面找到答案。找不到想了解的？欢迎通过邮箱联系我们。
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  问题分类
                </h2>
                <ul className="mt-4 space-y-2">
                  {faqGroups.map((group) => (
                    <li key={group}>
                      <a
                        href={`#${group}`}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-price-tag-3-line text-foreground-400"></i>
                        {group}
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-4 rounded-lg bg-secondary-50 border border-secondary-200">
                  <p className="text-xs leading-6 text-secondary-900">
                    仍有疑问？把你的问题发到 hello@5kanliao.cn，我们会在工作日回复。
                  </p>
                  <Link
                    to="/about#contact"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800 cursor-pointer"
                  >
                    前往反馈表单
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-12">
              {faqGroups.map((group) => {
                const items = faqs.filter((f) => f.group === group);
                if (items.length === 0) return null;
                return (
                  <section key={group} id={group} className="scroll-mt-24">
                    <div className="flex items-center gap-3">
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                        {group}
                      </h2>
                      <span className="px-2.5 py-0.5 rounded-full bg-background-100 text-xs text-foreground-500">
                        {items.length} 个问题
                      </span>
                    </div>
                    <div className="mt-5">
                      <FaqList items={items} />
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
      <JsonLd id="faq-page" data={faqLd} />
      <JsonLd id="faq-breadcrumb" data={breadcrumbLd} />
    </div>
  );
}